import DataGrid, { Column, Editing } from 'devextreme-react/data-grid';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge, Card, CardBody, Col, Input, Nav, NavItem, NavLink, Row } from 'reactstrap';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import { getSla, saveSlaRule } from './api';
import './devir-teslim.css';

// Operasyonlar > Operasyon İzleme > İstisnalar / SLA (rows 151, 153, 154): every open item of the process with its age
// against the rule's threshold (YAKLASIYOR from 80 %), computed from the existing records. Thresholds per rule are
// edited in the second tab (sla_write); changes are logged. Role group 'Operasyon İzleme - SLA' (022_sla_rules.sql).
const ROLE_GROUP = 35;
const STATE = { ASILDI: ['Aşıldı', 'danger'], YAKLASIYOR: ['Yaklaşıyor', 'warning'], OK: ['Süresinde', 'success'] };
const DETAIL_TAB = {
    ACIK_HASAR: '9',
    DEVIR_BEKLIYOR: '9',
    EMIR_CIKIS_YOK: '4',
    GRUPAJ_CIKMADI: '5',
    IADE_KARAR: '9',
    IADE_YOLDA: '9',
    TESLIM_YOK: '5',
};

const IstisnalarSla = () => {
    const { hasRole } = useAuth(ROLE_GROUP);
    const [tab, setTab] = useState('items');
    const [data, setData] = useState({ rules: [], items: [] });
    const [state, setState] = useState('');
    const [rule, setRule] = useState('');
    const load = useCallback(() => {
        getSla().then(setData).catch((e) => toast.error(e?.response?.data?.error || 'SLA listesi alınamadı'));
    }, []);
    useEffect(load, [load]);
    const persist = (code, values) => saveSlaRule(code, values)
        .then(() => { toast.success('Kaydedildi'); load(); return false; })
        .catch((e) => { toast.error(e?.response?.data?.error || 'Kaydedilemedi'); return true; });

    const count = (s, r) => data.items.filter((i) => (!s || i.state === s) && (!r || i.rule_code === r)).length;
    const rows = data.items.filter((i) => (!state || i.state === state) && (!rule || i.rule_code === rule));
    return (
        <div>
            <Breadcrumb parent="Operasyon İzleme" current="İstisnalar / SLA" />
            <Row className="mb-2">
                {[['', 'Açık kayıt', 'primary'], ['ASILDI', 'Süre aşıldı', 'danger'], ['YAKLASIYOR', 'Süresi yaklaşıyor', 'warning']].map(([s, t, c]) => (
                    <Col md="4" key={t}><Card style={{ cursor: 'pointer' }} onClick={() => { setTab('items'); setState(s); }}><CardBody className="py-3">
                        <div className="dt-muted">{t}</div><h3 className={`mb-0 text-${count(s) ? c : 'success'}`}>{count(s)}</h3>
                    </CardBody></Card></Col>
                ))}
            </Row>
            <Nav tabs className="border-tab nav-primary ci-tabs">
                {[['items', 'İstisna listesi'], ['rules', 'Kurallar (eşik süreleri)']].map(([k, t]) => (
                    <NavItem key={k}><NavLink className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>{t}</NavLink></NavItem>
                ))}
            </Nav>
            {tab === 'items' && (
                <Card><CardBody>
                    <div className="d-flex align-items-center mb-2" style={{ gap: 12 }}>
                        <Input bsSize="sm" type="select" style={{ maxWidth: 200 }} value={state} onChange={(e) => setState(e.target.value)}>
                            <option value="">Tüm durumlar</option>
                            {Object.entries(STATE).map(([k, [t]]) => <option key={k} value={k}>{t}</option>)}
                        </Input>
                        <Input bsSize="sm" type="select" style={{ maxWidth: 320 }} value={rule} onChange={(e) => setRule(e.target.value)}>
                            <option value="">Tüm kurallar</option>
                            {data.rules.filter((r) => r.is_active).map((r) => <option key={r.code} value={r.code}>{r.name_tr} ({count('', r.code)})</option>)}
                        </Input>
                        <span className="dt-muted">Süre, kuralın başladığı kayıttan bu yana; eşiğin %80'inde "yaklaşıyor".</span>
                    </div>
                    <table className="ct-table">
                        <thead><tr><th>Durum</th><th>Kural</th><th>VIN</th><th>Referans</th><th>Yer</th><th>Başlangıç</th><th>Süre / eşik</th><th>Sorumlu</th></tr></thead>
                        <tbody>{rows.map((i) => (
                            <tr key={`${i.rule_code}-${i.chassis_no}-${i.ref}`}>
                                <td><Badge color={STATE[i.state][1]}>{STATE[i.state][0]}</Badge></td>
                                <td>{i.rule_name}</td>
                                <td>{i.chassis_no ? <a className="font-weight-bold" target="_blank" rel="noopener noreferrer"
                                    title="Şase Sorgulama detayını aç"
                                    href={`${process.env.PUBLIC_URL}/operasyon/sase-sorgulama?vin=${encodeURIComponent(i.chassis_no)}&tab=${DETAIL_TAB[i.rule_code] || '3'}`}>
                                    {i.chassis_no}</a> : '—'}</td>
                                <td>{i.ref}</td>
                                <td>{i.place}</td>
                                <td>{moment(i.since).format('DD.MM.YYYY HH:mm')}</td>
                                <td className={i.state === 'ASILDI' ? 'ci-age-late' : ''}>{i.age_hours.toFixed(1)} / {i.threshold_hours} saat</td>
                                <td>{i.owner}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                    {rows.length === 0 && <div className="dt-muted p-2">Kayıt yok.</div>}
                </CardBody></Card>
            )}
            {tab === 'rules' && (
                <Card><CardBody>
                    <div className="dt-muted mb-2">Her kural mevcut kayıtlardan hesaplanır; eşik süresi, sorumlu ve açık/kapalı buradan değiştirilir, değişiklikler kim/ne zaman bilgisiyle loglanır.</div>
                    <DataGrid dataSource={data.rules} keyExpr="code" showBorders columnAutoWidth wordWrapEnabled
                        onRowUpdating={(e) => { e.cancel = persist(e.key, e.newData); }}>
                        <Editing mode="row" allowUpdating={hasRole('sla_write')} />
                        <Column dataField="code" caption="Kod" allowEditing={false} />
                        <Column dataField="name_tr" caption="Kural" />
                        <Column dataField="starts_at" caption="Süre başlar" allowEditing={false} />
                        <Column dataField="threshold_hours" caption="Eşik (saat)" dataType="number" />
                        <Column dataField="tolerance_hours" caption="Tolerans (saat)" dataType="number"
                            cellRender={(c) => (c.data.code === 'GEMI_SEFER' ? `${c.value ?? 0} (ETD→ETA'ya eklenir)` : '')} />
                        <Column dataField="owner" caption="Sorumlu" />
                        <Column dataField="is_active" caption="Aktif" dataType="boolean" />
                    </DataGrid>
                </CardBody></Card>
            )}
        </div>
    );
};

export default IstisnalarSla;
